import React from 'react';
import is from 'is';
import { isNullOrUndefinedOrEmpty } from 'rewire-common';
import { Observe } from 'rewire-core';
import { ICell } from 'rewire-grid';
import { withStyles } from 'rewire-ui';
import classNames from 'classnames';
import Divider from '@material-ui/core/Divider';
import { Theme } from '@material-ui/core/styles';
import { capitalize } from '../utility/StringUtils';
import { makeStyles } from '../utility/styles';
import { getTimeOffStatusColors, isMultiple } from '../DataTypes';
import { ITimeOffRequestText } from './TimeOffModel';

function defaultGetValue(cell: ICell): string {
  let value: string;
  if (is.array(cell.value)) {
    const valueToUse = cell.value.slice(0, 3);
    const values = cell.column.map ? cell.column.map(valueToUse) : valueToUse;
    value = cell.value.length > 3 ? `${values}, ....` : values;
  } else {
    value = cell.column.map ? cell.column.map(cell.value) : cell.value;
  }
  return value;
}

const defaultCellStyles = () => ({
  root: {
    padding: '4px',
  },
});

interface ICellProps {
  cell: ICell;
  classes: Record<string, string>;
}

export const TimeOffDefaultCell: React.SFC<ICellProps> = React.memo(
  withStyles(defaultCellStyles, (props) => {
    const { cell, classes } = props;
    let value: string | JSX.Element = defaultGetValue(cell);
    value = !isNullOrUndefinedOrEmpty(value) ? value : <span>&nbsp;</span>;
    return (
      <div className={classes.root} style={{ textAlign: cell.align }}>
        <div>{value}</div>
      </div>
    );
  }),
  () => false,
);

const employeeCellStyles = () => ({
  root: {
    fontSize: '1.15em',
    width: '100%',
    lineHeight: '1.5em',
    textDecoration: 'none',
    fontWeight: 500,
    padding: '4px',
  },
});

export const TimeOffEmployeeCell: React.SFC<ICellProps> = React.memo(
  withStyles(employeeCellStyles, (props) => {
    const { cell, classes } = props;
    return (
      <div className={classes.root} style={{ textAlign: cell.align }}>
        <div>{cell.value?.name ?? ''}</div>
      </div>
    );
  }),
  () => false,
);

const jobCellStyles = (theme: Theme) => ({
  root: {
    width: '100%',
    fontSize: '1.1em',
    lineHeight: '1.5em',
    padding: '4px',
  },
  occupation: {
    fontWeight: 700,
    color: theme.palette.dialogPrimary.main,
  },
  generator: {
    fontSize: '0.9em',
  },
  effective: {
    display: 'inline-block',
    fontSize: '0.85em',
    fontStyle: 'italic',
  },
  dateDivider: {
    padding: '0px 2px',
  },
});

export const TimeOffJobCell: React.SFC<ICellProps> = React.memo(
  withStyles(jobCellStyles, (props) => {
    const { cell, classes } = props;
    if (!cell.value) {
      return null;
    }
    if (isMultiple(cell.value)) {
      return (
        <div className={classes.root} style={{ textAlign: cell.align }}>
          {cell.value.name}
        </div>
      );
    }
    return (
      <Observe
        render={() => {
          const occupation = cell.value.text.occupation;
          const generator = cell.value.text.generator;
          const effectiveStart = cell.value.text.effectiveStart;
          const effectiveEnd = cell.value.text.effectiveEnd;
          return (
            <div className={classes.root} style={{ textAlign: cell.align }}>
              <div className={classes.occupation}>{occupation}</div>
              {generator && <div className={classes.generator}>{generator}</div>}
              <div className={classes.effective}>
                {effectiveStart && effectiveEnd && (
                  <>
                    <span>{effectiveStart}</span> <span className={classes.dateDivider}>to</span> <span>{effectiveEnd}</span>
                  </>
                )}
              </div>
            </div>
          );
        }}
      />
    );
  }),
  () => false,
);

const timeOffStatusStyles = (theme: Theme) => {
  const timeOffStatusColors = getTimeOffStatusColors(theme);

  return {
    requested: {
      color: timeOffStatusColors.requested,
    },
    approved: {
      color: timeOffStatusColors.approved,
    },
    onhold: {
      color: timeOffStatusColors.onhold,
    },
    declined: {
      color: timeOffStatusColors.declined,
    },
    cancelled: {
      color: timeOffStatusColors.cancelled,
    },
    published: {
      color: timeOffStatusColors.published,
    },
    accepted: {
      color: timeOffStatusColors.accepted,
    },
  };
};

const useTimeOffStatusStyle = makeStyles(timeOffStatusStyles);

const dateCellStyles = (theme: Theme) => ({
  root: {
    lineHeight: '1.5em',
    padding: '4px',
  },
  rootDayOff: {
    backgroundColor: theme.palette.neutralLight.main,
    height: '100%',
    width: '100%',
  },
  timeOffRangesContainer: {
    lineHeight: '1.3em',
  },
  statusContainer: {
    marginTop: '6px',
    fontWeight: 500,
  },
  torContainer: {
    padding: '2px 0px',
  },
  torDivider: {
    height: '2px',
    margin: '6px 0px',
    backgroundColor: theme.palette.primary.main,
  },
});

export const TimeOffDateCell: React.SFC<ICellProps> = React.memo(
  withStyles(dateCellStyles, (props) => {
    const { cell, classes } = props;
    const timeOffStatusClasses = useTimeOffStatusStyle();
    return (
      <Observe
        render={() => {
          const isDayOff = cell.value.isDayOff;
          const timeOffRequests = cell.value.timeOffRequests;
          const rootClasses = classNames(classes.root, isDayOff ? classes.rootDayOff : '');
          if (!timeOffRequests?.length) {
            return <div className={rootClasses} />;
          }
          const timeOffRequestsText = cell.value.timeOffRequestsText as ITimeOffRequestText[];
          return (
            <div className={rootClasses} style={{ textAlign: cell.align }}>
              {timeOffRequestsText.map((torText, torIdx) => {
                const timeOffStatusClassName = torText.status
                  .toLocaleLowerCase()
                  .replace(/\s/g, '') as keyof typeof timeOffStatusClasses;
                return (
                  <div key={torIdx as any}>
                    {torIdx !== 0 && <Divider className={classes.torDivider} />}
                    <div className={classes.torContainer}>
                      <div className={classes.timeOffRangesContainer}>
                        {torText.timeOffRanges.map((timeOffRangeText: string, idx: number) => {
                          return (
                            <div key={idx as any}>
                              <span>{timeOffRangeText}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div className={classNames(classes.statusContainer, timeOffStatusClasses[timeOffStatusClassName])}>
                        <span>{torText.status}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        }}
      />
    );
  }),
  () => false,
);

const statusCellStyles = () => ({
  root: {
    padding: '4px',
  },
});

export const TimeOffStatusCell: React.SFC<ICellProps> = React.memo(
  withStyles(statusCellStyles, (props) => {
    const { cell, classes } = props;
    const timeOffStatusClasses = useTimeOffStatusStyle();
    return (
      <Observe
        render={() => {
          const timeOffStatusClassName = cell.value?.replace(/\s/g, '') as keyof typeof timeOffStatusClasses;
          return (
            <div className={classes.root} style={{ textAlign: cell.align }}>
              <span className={timeOffStatusClasses[timeOffStatusClassName]}>{cell.value && capitalize(cell.value)}</span>
            </div>
          );
        }}
      />
    );
  }),
  () => false,
);
